import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(null);
  const [user, setUser] = useState(null);

  // Check if user is already logged in (token in localStorage)
  useEffect(() => {
    const checkAuth = async () => {
      const storedToken = localStorage.getItem('authToken');
      
      if (storedToken) {
        setToken(storedToken);
        
        try {
          // Verify token validity by fetching user data
          const response = await axios.get(
            `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/auth/user`,
            {
              headers: { Authorization: `Bearer ${storedToken}` }
            }
          );
          
          setUser(response.data);
          setIsAuthenticated(true);
        } catch (error) {
          console.error('Authentication error:', error);
          localStorage.removeItem('authToken');
          setToken(null);
          setIsAuthenticated(false);
        }
      }
      
      setLoading(false);
    };
    
    checkAuth();
  }, []);

  const login = (token) => {
    localStorage.setItem('authToken', token);
    setToken(token);
    setIsAuthenticated(true);
    
    // Fetch user data
    axios.get(
      `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/auth/user`,
      {
        headers: { Authorization: `Bearer ${token}` }
      }
    )
    .then(response => {
      setUser(response.data);
    })
    .catch(error => {
      console.error('Error fetching user data:', error);
    });
  };

  const logout = () => {
    localStorage.removeItem('authToken');
    setToken(null);
    setUser(null);
    setIsAuthenticated(false);
    // After logout, user will be redirected to login page via ProtectedRoute
  };

  const value = {
    isAuthenticated,
    loading,
    token,
    user,
    login,
    logout
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
